import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import { useWebsiteScrape } from "../../../contexts/WebsiteScrapeContext";

import ListWebsites from "./ListWebsites";
import ListTrainedData from "./ListTrainedData";
import ListArchivedData from "./ListArchivedData";
import ListWebsiteErrors from "./ListWebsiteErrors";
import AddSitemap from "../Sitemaps/AddSitemap";
import AddWebsite from "./AddWebsite";
import AddHomePage from "../Sitemaps/AddHomePage";
import AddBulkWebsiteLinks from "./AddBulkWebsiteLinks";
import ListSitemap from "../Sitemaps/ListSitemap";

import Modal from "react-modal";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Button,
  Drawer,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import MapIcon from "@mui/icons-material/Map";
import AddHomeIcon from "@mui/icons-material/Home";
import ListIcon from "@mui/icons-material/List";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";

Modal.setAppElement("#root");

export default function Scraper() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { chatbotId: paramsChatbotId } = useParams();
  const { registerOnComplete, unregisterOnComplete } = useWebsiteScrape();

  const [tabValue, setTabValue] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [isSitemapModalOpen, setIsSitemapModalOpen] = useState(false);
  const [isWebsiteModalOpen, setIsWebsiteModalOpen] = useState(false);
  const [isHomePageModalOpen, setIsHomePageModalOpen] = useState(false);
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    document.title = "Scraper | KlonIT Web Application";
  }, []);

  // Refresh lists when scraping completes
  useEffect(() => {
    registerOnComplete(() => {
      setRefresh((prev) => !prev);
    });
    return () => unregisterOnComplete();
  }, [registerOnComplete, unregisterOnComplete]);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("website", "view");
        const modifyPerm = await hasPermission("website", "add|edit");
        const deletePerm = await hasPermission("website", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  const handleRefresh = useCallback(() => {
    setRefresh((prev) => !prev);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  // Loading state
  if (checkingPermissions) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}
        sx={{ margin: "0 auto", maxWidth: 400, minHeight: "50vh" }}
      >
        <CircularProgress size={30} />
        <Typography mt={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Access Denied
  if (!canView) {
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 10 }, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6" fontWeight={700}>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to view website data. Please contact your administrator.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          mt: { xs: 2, sm: 4, md: 10 },
          mx: "auto",
          maxWidth: "1300px",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: { xs: 1.5, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.5rem' } }} color="textPrimary">
          Manage <strong>Website</strong>
        </Typography>
        <Divider sx={{ mb: { xs: 1.5, sm: 3 } }} />

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          useFlexGap
          sx={{ mb: { xs: 1.5, sm: 3 }, gap: { xs: 0.5, sm: 1 } }}
        >
          {canModify && (
            <>
              <Button
                variant="contained"
                startIcon={<MapIcon />}
                onClick={() => setIsSitemapModalOpen(true)}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                {isMobile ? 'Sitemap' : 'Add Sitemap'}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddHomeIcon />}
                onClick={() => setIsHomePageModalOpen(true)}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                {isMobile ? 'Homepage' : 'Add Homepage'}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddLinkIcon />}
                onClick={() => setIsWebsiteModalOpen(true)}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                {isMobile ? 'Add Links' : 'Add Website Links'}
              </Button>

              <Button
                variant="contained"
                startIcon={<LibraryAddIcon />}
                onClick={() => setIsBulkAddModalOpen(true)}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                {isMobile ? 'Bulk Add' : 'Add Bulk Website Links'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<ListIcon />}
                onClick={openDrawer}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                {isMobile ? 'Sitemaps' : 'List Sitemap'}
              </Button>
            </>
          )}
        </Stack>

        {/* Tabs */}
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="website scraper tabs"
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                textAlign: 'left',
                px: { xs: 1.5, sm: 3 },
                py: { xs: 1, sm: 2 },
                justifyContent: 'flex-start',
                color: `${theme.palette.text.primary} !important`,
                transition: 'all 0.2s ease',
                minHeight: { xs: 40, sm: 48 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 'auto', sm: 90 },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.text.primary,
                },
              },
              '& .MuiTab-root.Mui-selected': {
                color: `${theme.palette.primary.main} !important`,
                backgroundColor: theme.palette.action.selected,
                fontWeight: 'bold',
                '& .MuiSvgIcon-root': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label={isMobile ? "Unscraped" : "Unscraped Data"} />
            <Tab label={isMobile ? "Errors" : "Error Data"} />
            <Tab label={isMobile ? "Trained" : "Trained Data"} />
            <Tab label={isMobile ? "Archived" : "Archive Data"} />
          </Tabs>

          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {tabValue === 0 && (
              <ListWebsites
                refresh={refresh}
                setRefresh={setRefresh}
                chatbotId={paramsChatbotId}
                permission={{ canView, canModify, canDelete }}
              />
            )}
            {tabValue === 1 && (
              <ListWebsiteErrors
                refresh={refresh}
                setRefresh={setRefresh}
                chatbotId={paramsChatbotId}
                permission={{ canView, canModify, canDelete }}
              />
            )}
            {tabValue === 2 && (
              <ListTrainedData
                refresh={refresh}
                setRefresh={setRefresh}
                chatbotId={paramsChatbotId}
                permission={{ canView, canModify, canDelete }}
              />
            )}
            {tabValue === 3 && (
              <ListArchivedData
                refresh={refresh}
                setRefresh={setRefresh}
                chatbotId={paramsChatbotId}
                permission={{ canView, canModify, canDelete }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Drawer - List Sitemap */}
      <Drawer anchor="right" open={isDrawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: { xs: "90vw", sm: "70vw", md: "50vw" }, p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            List Sitemap
          </Typography>
          <ListSitemap
            refresh={refresh}
            setRefresh={setRefresh}
            chatbotId={paramsChatbotId}
            permission={{ canView, canModify, canDelete }}
          />
        </Box>
      </Drawer>

      {/* React Modals */}
      <Modal
        isOpen={isSitemapModalOpen}
        onRequestClose={() => setIsSitemapModalOpen(false)}
        contentLabel="Add Sitemap Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <AddSitemap
          closeModal={() => setIsSitemapModalOpen(false)}
          refresh={refresh}
          handleRefresh={handleRefresh}
          chatbotId={paramsChatbotId}
          permission={{ canView, canModify, canDelete }}
        />
      </Modal>

      <Modal
        isOpen={isHomePageModalOpen}
        onRequestClose={() => setIsHomePageModalOpen(false)}
        contentLabel="Add Homepage Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <AddHomePage
          closeModal={() => setIsHomePageModalOpen(false)}
          refresh={refresh}
          handleRefresh={handleRefresh}
          chatbotId={paramsChatbotId}
          permission={{ canView, canModify, canDelete }}
        />
      </Modal>

      <Modal
        isOpen={isWebsiteModalOpen}
        onRequestClose={() => setIsWebsiteModalOpen(false)}
        contentLabel="Add Website Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <AddWebsite
          closeModal={() => setIsWebsiteModalOpen(false)}
          refresh={refresh}
          handleRefresh={handleRefresh}
          chatbotId={paramsChatbotId}
          permission={{ canView, canModify, canDelete }}
        />
      </Modal>

      <Modal
        isOpen={isBulkAddModalOpen}
        onRequestClose={() => setIsBulkAddModalOpen(false)}
        contentLabel="Add Bulk Website Links Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <AddBulkWebsiteLinks
          closeModal={() => setIsBulkAddModalOpen(false)}
          refresh={refresh}
          handleRefresh={handleRefresh}
          chatbotId={paramsChatbotId}
          permission={{ canView, canModify, canDelete }}
        />
      </Modal>

      {/* MUI-Compatible Modal Styling */}
      <style jsx>{`
        .Modal {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 95%;
          max-width: 700px;
          background-color: var(--mui-palette-background-paper);
          border-radius: 12px;
          padding: 24px;
          outline: none;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
        }
        @media (min-width: 600px) {
          .Modal {
            width: 90%;
            padding: 32px;
          }
        }
        .Overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          z-index: 1200;
          backdrop-filter: blur(3px);
        }
      `}</style>
    </>
  );
}
